import { bus } from '../core/event-bus.js';
import { store } from '../core/state-store.js';
import { playerManager } from '../core/player-manager.js';
import {
  getProfile,
  buildCustomProfile,
  getCustomOverrideDefaults,
  getCustomBaseOptions,
  normalizeLegacyEnvironmentName,
} from './profiles.js';
import { updateUrlParams } from '../automation/url-params.js';

let currentEnv = 'desktop';
let customOverrides = getCustomOverrideDefaults();

let customPanel;
let customContent;
let customInputs;
let customApplyBtn;
let customResetBtn;
let customToggleBtn;
let customCollapsed = false;
let previewViewport;
let previewStage;
let previewResizeObserver;
let previewScaleRaf = null;
let emulatedPlayerSize = { width: 960, height: 540 };

export function initSimulator(initialConfig = {}) {
  customPanel = document.getElementById('custom-env-panel');
  customContent = document.getElementById('custom-env-content');
  customApplyBtn = document.getElementById('custom-apply-btn');
  customResetBtn = document.getElementById('custom-reset-btn');
  customToggleBtn = document.getElementById('custom-env-toggle-btn');
  previewViewport = document.getElementById('player-preview-viewport');
  previewStage = document.getElementById('player-preview-stage');
  customInputs = {
    base: document.getElementById('custom-base-select'),
    playerWidth: document.getElementById('custom-width-input'),
    playerWidthRange: document.getElementById('custom-width-range'),
    playerHeight: document.getElementById('custom-height-input'),
    playerHeightRange: document.getElementById('custom-height-range'),
    autoplay: document.getElementById('custom-autoplay-input'),
    muted: document.getElementById('custom-muted-input'),
    outstream: document.getElementById('custom-outstream-input'),
    ajaxWithCredentials: document.getElementById('custom-credentials-input'),
    showControlsForAdPlayer: document.getElementById('custom-controls-input'),
    vpaidViewMode: document.getElementById('custom-vpaid-view-select'),
    ajaxTimeout: document.getElementById('custom-ajax-timeout-input'),
    ajaxTimeoutRange: document.getElementById('custom-ajax-timeout-range'),
    creativeLoadTimeout: document.getElementById('custom-creative-timeout-input'),
    creativeLoadTimeoutRange: document.getElementById('custom-creative-timeout-range'),
  };

  customOverrides = _sanitizeCustomOverrides(initialConfig.customEnv || {});
  _syncCustomInputsFromOverrides();
  _wireSliderSync();
  _syncCustomPanelCollapsedState();
  _initPreviewScaling();

  bus.on('env:select', (envSelection) => {
    applyEnvironment(envSelection);
  });

  // Wire environment buttons
  const buttons = document.querySelectorAll('[data-env]');
  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      applyEnvironment(btn.getAttribute('data-env'));
    });
  });

  customApplyBtn?.addEventListener('click', async () => {
    const fromInputs = _readCustomOverridesFromInputs();
    await applyEnvironment({ env: 'custom', customOverrides: fromInputs });
  });

  customInputs?.base?.addEventListener('change', () => {
    _applySelectedBaseToCustomOverrides(customInputs.base.value);
  });

  customResetBtn?.addEventListener('click', async () => {
    customOverrides = getCustomOverrideDefaults();
    _syncCustomInputsFromOverrides();
    if (currentEnv === 'custom') {
      await applyEnvironment({ env: 'custom', customOverrides });
    }
  });

  customToggleBtn?.addEventListener('click', () => {
    customCollapsed = !customCollapsed;
    _syncCustomPanelCollapsedState();
    bus.emit('layout:top-panels-content-changed');
  });
}

export async function applyEnvironment(envSelection) {
  const { envName, incomingCustom, profileOverrides } = _normalizeEnvSelection(envSelection);
  if (incomingCustom) {
    customOverrides = _sanitizeCustomOverrides(incomingCustom);
  }

  const profile = envName === 'custom'
    ? buildCustomProfile(customOverrides)
    : getProfile(envName);
  const resolvedProfile = {
    ...profile,
    ...(profileOverrides || {}),
  };

  if (envName !== 'custom') {
    customOverrides = _profileToCustomOverrides(resolvedProfile, envName);
  }
  currentEnv = envName;

  // Update UI
  document.querySelectorAll('[data-env]').forEach((btn) => {
    btn.classList.toggle('active', btn.getAttribute('data-env') === envName);
  });
  _toggleCustomPanel(envName === 'custom');
  _syncCustomPanelCollapsedState();
  bus.emit('layout:top-panels-content-changed');
  _syncCustomInputsFromOverrides();

  const badge = document.querySelector('[data-testid="env-badge"]');
  if (badge) badge.textContent = resolvedProfile.name;

  // Keep emulated player size independent from available panel/viewport size.
  _setEmulatedPlayerSize(resolvedProfile.playerWidth, resolvedProfile.playerHeight);

  // Keep tag-bar auto-start UI in sync with active environment autoplay
  const autoStartToggle = document.getElementById('auto-start-toggle');
  if (autoStartToggle) {
    autoStartToggle.checked = Boolean(resolvedProfile.autoplay);
  }

  store.update({ environment: envName });
  updateUrlParams({
    env: envName,
    customEnv: envName === 'custom' ? customOverrides : null,
  });

  // Re-initialize player with new params
  await playerManager.init(resolvedProfile);
  // Re-apply size in case player init adjusts container dimensions internally.
  _setEmulatedPlayerSize(resolvedProfile.playerWidth, resolvedProfile.playerHeight);

  bus.emit('env:applied', {
    name: envName,
    profile: resolvedProfile,
    customOverrides: envName === 'custom' ? customOverrides : null,
  });
}

export function getCurrentEnvironment() {
  return currentEnv;
}

function _normalizeEnvSelection(envSelection) {
  if (typeof envSelection === 'string') {
    return {
      envName: normalizeLegacyEnvironmentName(envSelection),
      incomingCustom: null,
      profileOverrides: null,
    };
  }

  if (envSelection && typeof envSelection === 'object') {
    const envName = normalizeLegacyEnvironmentName(envSelection.env || envSelection.name || 'desktop');
    return {
      envName,
      incomingCustom: envSelection.customOverrides || envSelection.overrides || null,
      profileOverrides: envSelection.profileOverrides || null,
    };
  }

  return { envName: 'desktop', incomingCustom: null, profileOverrides: null };
}

function _sanitizeCustomOverrides(overrides = {}) {
  const defaults = getCustomOverrideDefaults();
  const merged = { ...defaults, ...overrides };
  const allowedBases = getCustomBaseOptions();

  return {
    base: allowedBases.includes(merged.base) ? merged.base : defaults.base,
    playerWidth: _toPositiveNumber(merged.playerWidth, defaults.playerWidth),
    playerHeight: _toPositiveNumber(merged.playerHeight, defaults.playerHeight),
    autoplay: _toBoolean(merged.autoplay, defaults.autoplay),
    muted: _toBoolean(merged.muted, defaults.muted),
    outstream: _toBoolean(merged.outstream, defaults.outstream),
    ajaxWithCredentials: _toBoolean(merged.ajaxWithCredentials, defaults.ajaxWithCredentials),
    showControlsForAdPlayer: _toBoolean(merged.showControlsForAdPlayer, defaults.showControlsForAdPlayer),
    vpaidViewMode: merged.vpaidViewMode === 'fullscreen' ? 'fullscreen' : 'normal',
    ajaxTimeout: _toPositiveNumber(merged.ajaxTimeout, defaults.ajaxTimeout),
    creativeLoadTimeout: _toPositiveNumber(merged.creativeLoadTimeout, defaults.creativeLoadTimeout),
  };
}

function _readCustomOverridesFromInputs() {
  if (!customInputs) return customOverrides;
  return _sanitizeCustomOverrides({
    base: customInputs.base?.value,
    playerWidth: customInputs.playerWidth?.value,
    playerHeight: customInputs.playerHeight?.value,
    autoplay: customInputs.autoplay?.checked,
    muted: customInputs.muted?.checked,
    outstream: customInputs.outstream?.checked,
    ajaxWithCredentials: customInputs.ajaxWithCredentials?.checked,
    showControlsForAdPlayer: customInputs.showControlsForAdPlayer?.checked,
    vpaidViewMode: customInputs.vpaidViewMode?.value,
    ajaxTimeout: customInputs.ajaxTimeout?.value,
    creativeLoadTimeout: customInputs.creativeLoadTimeout?.value,
  });
}

function _syncCustomInputsFromOverrides() {
  if (!customInputs) return;
  customInputs.base && (customInputs.base.value = customOverrides.base);
  _setNumericPairValue(customInputs.playerWidth, customInputs.playerWidthRange, customOverrides.playerWidth);
  _setNumericPairValue(customInputs.playerHeight, customInputs.playerHeightRange, customOverrides.playerHeight);
  customInputs.autoplay && (customInputs.autoplay.checked = customOverrides.autoplay);
  customInputs.muted && (customInputs.muted.checked = customOverrides.muted);
  customInputs.outstream && (customInputs.outstream.checked = customOverrides.outstream);
  customInputs.ajaxWithCredentials && (customInputs.ajaxWithCredentials.checked = customOverrides.ajaxWithCredentials);
  customInputs.showControlsForAdPlayer && (customInputs.showControlsForAdPlayer.checked = customOverrides.showControlsForAdPlayer);
  customInputs.vpaidViewMode && (customInputs.vpaidViewMode.value = customOverrides.vpaidViewMode);
  _setNumericPairValue(customInputs.ajaxTimeout, customInputs.ajaxTimeoutRange, customOverrides.ajaxTimeout);
  _setNumericPairValue(customInputs.creativeLoadTimeout, customInputs.creativeLoadTimeoutRange, customOverrides.creativeLoadTimeout);
}

function _profileToCustomOverrides(profile, baseName = 'desktop') {
  return _sanitizeCustomOverrides({
    base: baseName,
    playerWidth: profile.playerWidth,
    playerHeight: profile.playerHeight,
    autoplay: profile.autoplay,
    muted: profile.muted,
    outstream: profile.outstream,
    ajaxWithCredentials: profile.ajaxWithCredentials,
    showControlsForAdPlayer: profile.showControlsForAdPlayer,
    vpaidViewMode: profile.vpaidViewMode,
    ajaxTimeout: profile.ajaxTimeout,
    creativeLoadTimeout: profile.creativeLoadTimeout,
  });
}

function _applySelectedBaseToCustomOverrides(baseName) {
  const normalizedBase = getCustomBaseOptions().includes(baseName)
    ? baseName
    : getCustomOverrideDefaults().base;
  const baseProfile = getProfile(normalizedBase);
  customOverrides = _profileToCustomOverrides(baseProfile, normalizedBase);
  _syncCustomInputsFromOverrides();
}

function _toggleCustomPanel(visible) {
  if (!customPanel) return;
  customPanel.classList.toggle('hidden', !visible);
}

function _syncCustomPanelCollapsedState() {
  if (!customPanel) return;
  customPanel.classList.toggle('is-collapsed', customCollapsed);
  if (customContent) {
    customContent.classList.toggle('hidden', customCollapsed);
  }
  if (customToggleBtn) {
    customToggleBtn.textContent = customCollapsed ? 'Show' : 'Hide';
    customToggleBtn.setAttribute('aria-expanded', customCollapsed ? 'false' : 'true');
  }
}

function _toPositiveNumber(value, fallback) {
  const asNum = Number(value);
  return Number.isFinite(asNum) && asNum > 0 ? Math.round(asNum) : fallback;
}

function _toBoolean(value, fallback) {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function _wireSliderSync() {
  _wireNumericPair(customInputs?.playerWidth, customInputs?.playerWidthRange);
  _wireNumericPair(customInputs?.playerHeight, customInputs?.playerHeightRange);
  _wireNumericPair(customInputs?.ajaxTimeout, customInputs?.ajaxTimeoutRange);
  _wireNumericPair(customInputs?.creativeLoadTimeout, customInputs?.creativeLoadTimeoutRange);
}

function _wireNumericPair(numberInput, rangeInput) {
  if (!numberInput || !rangeInput) return;
  numberInput.addEventListener('input', () => {
    const normalized = _clampToRange(numberInput.value, rangeInput);
    rangeInput.value = String(normalized);
  });
  rangeInput.addEventListener('input', () => {
    numberInput.value = rangeInput.value;
  });
}

function _setNumericPairValue(numberInput, rangeInput, value) {
  if (numberInput) numberInput.value = value;
  if (rangeInput) {
    rangeInput.value = String(_clampToRange(value, rangeInput));
  }
}

function _clampToRange(value, rangeInput) {
  const min = Number(rangeInput.min || 0);
  const max = Number(rangeInput.max || Number.MAX_SAFE_INTEGER);
  const parsed = Number(value);
  const fallback = Number(rangeInput.value || min || 0);
  const base = Number.isFinite(parsed) ? parsed : fallback;
  return Math.min(max, Math.max(min, base));
}

function _initPreviewScaling() {
  if (!previewViewport || !previewStage) return;

  if (previewResizeObserver) {
    previewResizeObserver.disconnect();
    previewResizeObserver = null;
  }

  if (typeof ResizeObserver !== 'undefined') {
    previewResizeObserver = new ResizeObserver(() => {
      _schedulePreviewScale();
    });
    previewResizeObserver.observe(previewViewport);
  }

  window.addEventListener('resize', _schedulePreviewScale);
  document.addEventListener('fullscreenchange', _schedulePreviewScale);
  _schedulePreviewScale();
}

function _setEmulatedPlayerSize(width, height) {
  const normalizedWidth = _toPositiveNumber(width, emulatedPlayerSize.width);
  const normalizedHeight = _toPositiveNumber(height, emulatedPlayerSize.height);
  emulatedPlayerSize = {
    width: normalizedWidth,
    height: normalizedHeight,
  };

  const container = document.getElementById('vast-player');
  if (container) {
    container.style.width = `${normalizedWidth}px`;
    container.style.height = `${normalizedHeight}px`;
  }
  if (previewStage) {
    previewStage.style.width = `${normalizedWidth}px`;
    previewStage.style.height = `${normalizedHeight}px`;
  }

  _schedulePreviewScale();
}

function _schedulePreviewScale() {
  if (previewScaleRaf !== null) {
    cancelAnimationFrame(previewScaleRaf);
  }
  previewScaleRaf = requestAnimationFrame(() => {
    previewScaleRaf = null;
    _applyPreviewScale();
  });
}

function _applyPreviewScale() {
  if (!previewViewport || !previewStage) return;

  const width = emulatedPlayerSize.width;
  const height = emulatedPlayerSize.height;
  if (!width || !height) return;

  const availableWidth = Math.max(0, previewViewport.clientWidth);
  const availableHeight = Math.max(0, previewViewport.clientHeight);
  if (!availableWidth || !availableHeight) return;

  const scale = Math.min(availableWidth / width, availableHeight / height, 1);
  previewStage.style.transform = `scale(${scale})`;
  previewViewport.setAttribute('data-preview-scale', scale.toFixed(4));
}
